/**
 * Gift Wrapping Functionality
 * Handles adding/removing gift wrapping products for items with "kadobon" tag
 */

(function() {
  'use strict';

  class GiftWrapping {
    constructor(container) {
      this.container = container;
      this.config = this.getConfig();
      
      if (!this.config) {
        console.warn('Gift wrapping config not found');
        return;
      }
      
      this.isLoading = false;
      this.bindEvents();
      this.updateUI();
      
      // Check for orphaned gift wraps on page load
      this.removeOrphanedGiftWraps();
      
      // Listen for cart quantity changes to reload and sync gift wrapping options
      this.listenForCartUpdates();
    }
    
    getConfig() {
      const configEl = this.container.querySelector('[data-gift-wrap-config]');
      if (!configEl) return null;
      
      try {
        return JSON.parse(configEl.textContent);
      } catch (e) {
        console.error('Failed to parse gift wrap config:', e);
        return null;
      }
    }
    
    bindEvents() {
      // Toggle change form button
      const toggleBtn = this.container.querySelector('[data-toggle-change-wrap]');
      if (toggleBtn) {
        toggleBtn.addEventListener('click', this.handleToggleChangeForm.bind(this));
      }
      
      // Mode selector (combined vs separate)
      const modeSelect = this.container.querySelector('[data-wrap-mode-select]');
      if (modeSelect) {
        modeSelect.addEventListener('change', this.handleModeChange.bind(this));
      }
      
      // All wrapping selects - update UI on change
      const allSelects = this.container.querySelectorAll('.gift-wrapping__select');
      allSelects.forEach(select => {
        select.addEventListener('change', this.updateUI.bind(this));
      });
      
      // Add to cart button
      const addBtn = this.container.querySelector('[data-add-gift-wrap]');
      if (addBtn) {
        addBtn.addEventListener('click', this.handleAddToCart.bind(this));
      }
    }
    
    handleToggleChangeForm() {
      const selectedSection = this.container.querySelector('[data-wrap-selected]');
      const changeForm = this.container.querySelector('[data-wrap-change-form]');
      
      if (selectedSection && changeForm) {
        selectedSection.classList.toggle('hide');
        changeForm.classList.toggle('hide');
      }
    }
    
    handleModeChange(event) {
      const mode = event.target.value;
      const combinedWrap = this.container.querySelector('[data-combined-wrap]');
      const separateWrap = this.container.querySelector('[data-separate-wrap]');
      
      // Reset selections when mode changes
      if (combinedWrap) {
        const combinedSelect = combinedWrap.querySelector('select');
        if (combinedSelect) combinedSelect.value = '';
      }
      
      if (separateWrap) {
        const separateSelects = separateWrap.querySelectorAll('select');
        separateSelects.forEach(select => select.value = '');
      }
      
      // Hide all first
      if (combinedWrap) combinedWrap.classList.add('hide');
      if (separateWrap) separateWrap.classList.add('hide');
      
      // Show selected mode
      if (mode === 'combined') {
        if (combinedWrap) combinedWrap.classList.remove('hide');
      } else if (mode === 'separate') {
        if (separateWrap) separateWrap.classList.remove('hide');
      }
      
      this.updateUI();
    }
    
    updateUI() {
      const totalEl = this.container.querySelector('[data-wrap-total]');
      const totalPriceEl = this.container.querySelector('[data-wrap-total-price]');
      const actionsEl = this.container.querySelector('[data-wrap-actions]');
      
      let total = 0;
      let hasSelection = false;
      
      // Check single product select
      const singleSelect = this.container.querySelector('[data-single-wrap] [data-gift-wrap-select]');
      if (singleSelect && singleSelect.value) {
        const selectedOption = singleSelect.options[singleSelect.selectedIndex];
        const price = parseInt(selectedOption.dataset.price) || 0;
        total += price;
        hasSelection = true;
      }
      
      // Check mode selector
      const modeSelect = this.container.querySelector('[data-wrap-mode-select]');
      if (modeSelect) {
        const mode = modeSelect.value;
        
        if (mode === 'combined') {
          const combinedSelect = this.container.querySelector('[data-gift-wrap-combined]');
          if (combinedSelect && combinedSelect.value) {
            const selectedOption = combinedSelect.options[combinedSelect.selectedIndex];
            const price = parseInt(selectedOption.dataset.price) || 0;
            total += price;
            hasSelection = true;
          }
        } else if (mode === 'separate') {
          const separateSelects = this.container.querySelectorAll('[data-separate-wrap] [data-gift-wrap-select]');
          separateSelects.forEach(select => {
            if (select.value) {
              const selectedOption = select.options[select.selectedIndex];
              const price = parseInt(selectedOption.dataset.price) || 0;
              total += price;
              hasSelection = true;
            }
          });
        }
      }
      
      // Update total display
      if (totalEl && totalPriceEl) {
        if (total > 0) {
          totalEl.classList.remove('hide');
          totalPriceEl.textContent = this.formatMoney(total);
        } else {
          totalEl.classList.add('hide');
        }
      }
      
      // Show/hide add button
      if (actionsEl) {
        if (hasSelection) {
          actionsEl.classList.remove('hide');
        } else {
          actionsEl.classList.add('hide');
        }
      }
      
      // Hide status message when selections change
      this.hideStatus();
    }
    
    async handleAddToCart(event) {
      event.preventDefault();
      
      if (this.isLoading) return;
      
      const addBtn = this.container.querySelector('[data-add-gift-wrap]');
      const btnText = addBtn.querySelector('.gift-wrapping__btn-text');
      const btnLoading = addBtn.querySelector('.gift-wrapping__btn-loading');
      
      // Set loading state
      this.isLoading = true;
      addBtn.disabled = true;
      btnText.classList.add('hide');
      btnLoading.classList.remove('hide');
      
      const isChanging = this.config.existingWrapCount > 0;
      
      try {
        // First, remove any existing gift wrap items
        await this.removeExistingGiftWraps();
        
        // Collect items to add
        const itemsToAdd = this.collectItemsToAdd();
        
        if (itemsToAdd.length > 0) {
          // Add new gift wrap items
          await this.addItemsToCart(itemsToAdd);
        }
        
        // Show success message using translated string
        const successMessage = isChanging 
          ? this.config.translations.successChanged 
          : this.config.translations.successAdded;
        this.showStatus(successMessage, 'success');
        
        // Reload after short delay so user sees the message
        setTimeout(() => {
          window.location.reload();
        }, 800);
        
      } catch (error) {
        console.error('Error updating gift wrap:', error);
        this.showStatus(this.config.translations.errorMessage, 'error');
        
        // Reset button state
        this.isLoading = false;
        addBtn.disabled = false;
        btnText.classList.remove('hide');
        btnLoading.classList.add('hide');
      }
    }
    
    getProductTitleFromSelect(select) {
      // Find the product name from the parent wrapper
      const wrapper = select.closest('[data-wrap-item]') || select.closest('[data-single-wrap]');
      if (wrapper) {
        const nameEl = wrapper.querySelector('.gift-wrapping__product-name');
        if (nameEl) {
          return nameEl.textContent.trim();
        }
      }
      return null;
    }
    
    getAllProductTitles() {
      // Get all product titles for combined wrapping
      const titles = [];
      const nameEls = this.container.querySelectorAll('[data-separate-wrap] .gift-wrapping__product-name, [data-single-wrap] .gift-wrapping__product-name');
      nameEls.forEach(el => {
        const title = el.textContent.trim();
        if (title) titles.push(title);
      });
      return titles;
    }
    
    collectItemsToAdd() {
      const items = [];
      const translations = this.config.translations;
      
      // Single product mode
      const singleSelect = this.container.querySelector('[data-single-wrap] [data-gift-wrap-select]');
      if (singleSelect && singleSelect.value) {
        const productTitle = this.getProductTitleFromSelect(singleSelect);
        const qtyIndex = singleSelect.dataset.qtyIndex || '1';
        items.push({
          id: parseInt(singleSelect.value),
          quantity: 1,
          properties: {
            [translations.forProduct]: productTitle || translations.unknownProduct,
            '_linked_product_id': singleSelect.dataset.productId + '_' + qtyIndex,
            '_wrap_type': 'single'
          }
        });
        return items;
      }
      
      // Multiple products mode
      const modeSelect = this.container.querySelector('[data-wrap-mode-select]');
      if (!modeSelect) return items;
      
      const mode = modeSelect.value;
      
      if (mode === 'combined') {
        const combinedSelect = this.container.querySelector('[data-gift-wrap-combined]');
        if (combinedSelect && combinedSelect.value) {
          const allTitles = this.getAllProductTitles();
          const combinedLabel = allTitles.length > 0 ? allTitles.join(' + ') : translations.allProducts;
          items.push({
            id: parseInt(combinedSelect.value),
            quantity: 1,
            properties: {
              [translations.forProduct]: combinedLabel,
              '_linked_product_id': 'combined',
              '_wrap_type': 'combined'
            }
          });
        }
      } else if (mode === 'separate') {
        const separateSelects = this.container.querySelectorAll('[data-separate-wrap] [data-gift-wrap-select]');
        separateSelects.forEach(select => {
          if (select.value) {
            const productTitle = this.getProductTitleFromSelect(select);
            const qtyIndex = select.dataset.qtyIndex || '1';
            items.push({
              id: parseInt(select.value),
              quantity: 1,
              properties: {
                [translations.forProduct]: productTitle || translations.unknownProduct,
                '_linked_product_id': select.dataset.productId + '_' + qtyIndex,
                '_wrap_type': 'separate'
              }
            });
          }
        });
      }
      
      return items;
    }
    
    async removeExistingGiftWraps() {
      try {
        const response = await fetch(this.config.routes.cart + '.js');
        const cart = await response.json();
        
        const giftWrapItems = cart.items.filter(item => 
          item.product_id === this.config.giftWrapProductId
        );
        
        // Remove each gift wrap item
        for (const item of giftWrapItems) {
          await fetch(this.config.routes.cartChange + '.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              id: item.key,
              quantity: 0
            })
          });
        }
      } catch (error) {
        console.error('Error removing existing gift wraps:', error);
        // Continue even if removal fails
      }
    }
    
    async addItemsToCart(items) {
      const response = await fetch(this.config.routes.cartAdd + '.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ items: items })
      });
      
      if (!response.ok) {
        throw new Error('Failed to add items to cart');
      }
      
      return response.json();
    }
    
    showStatus(message, type) {
      const statusEl = this.container.querySelector('[data-wrap-status]');
      if (!statusEl) return;
      
      statusEl.textContent = message;
      statusEl.className = 'gift-wrapping__status gift-wrapping__status--' + type;
      statusEl.classList.remove('hide');
    }
    
    hideStatus() {
      const statusEl = this.container.querySelector('[data-wrap-status]');
      if (statusEl) {
        statusEl.classList.add('hide');
      }
    }
    
    formatMoney(cents) {
      // Use Shopify's money format if available
      if (this.config && this.config.moneyFormat) {
        const amount = (cents / 100).toFixed(2);
        return this.config.moneyFormat
          .replace('{{amount}}', amount)
          .replace('{{amount_no_decimals}}', Math.round(cents / 100))
          .replace('{{amount_with_comma_separator}}', amount.replace('.', ','))
          .replace('{{amount_no_decimals_with_comma_separator}}', Math.round(cents / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.'));
      }
      // Fallback
      const euros = (cents / 100).toFixed(2).replace('.', ',');
      return '€' + euros;
    }
    
    /**
     * Remove orphaned gift wrap items when linked product is no longer in cart
     */
    async removeOrphanedGiftWraps() {
      try {
        const response = await fetch(this.config.routes.cart + '.js');
        const cart = await response.json();
        
        // Get all gift wrap items
        const giftWrapItems = cart.items.filter(item => 
          item.product_id === this.config.giftWrapProductId
        );
        
        if (giftWrapItems.length === 0) return;
        
        // Get all kadobon product IDs currently in cart (including qty index)
        const kadobonProductIds = new Set();
        let hasAnyKadobonItems = false;
        
        cart.items.forEach(item => {
          if (item.product_id !== this.config.giftWrapProductId) {
            // Check if it's a kadobon item by checking properties or using kadobonItems from config
            const isKadobon = this.config.kadobonItems.some(kadobonData => {
              // kadobonData format: "itemKey::title::productId::qtyIndex"
              const parts = kadobonData.split('::');
              return parts[2] && parseInt(parts[2]) === item.product_id;
            });
            
            if (isKadobon) {
              hasAnyKadobonItems = true;
              // Add each quantity index for this product
              for (let i = 1; i <= item.quantity; i++) {
                kadobonProductIds.add(item.product_id + '_' + i);
              }
            }
          }
        });
        
        // Find orphaned gift wraps
        const orphanedItems = [];
        
        giftWrapItems.forEach(giftWrap => {
          const linkedProductId = giftWrap.properties?._linked_product_id;
          
          if (!linkedProductId) {
            // No linked product - orphaned
            orphanedItems.push(giftWrap.key);
          } else if (linkedProductId === 'combined') {
            // Combined wrapping - check if ANY kadobon items exist
            if (!hasAnyKadobonItems) {
              orphanedItems.push(giftWrap.key);
            }
          } else {
            // Single/separate wrapping - check if specific product exists
            if (!kadobonProductIds.has(linkedProductId)) {
              orphanedItems.push(giftWrap.key);
            }
          }
        });
        
        // Remove orphaned items
        if (orphanedItems.length > 0) {
          console.log('Removing orphaned gift wraps:', orphanedItems);
          
          for (const itemKey of orphanedItems) {
            await fetch(this.config.routes.cartChange + '.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                id: itemKey,
                quantity: 0
              })
            });
          }
          
          // Reload to reflect changes
          window.location.reload();
        }
      } catch (error) {
        console.error('Error checking for orphaned gift wraps:', error);
      }
    }
    
    /**
     * Listen for cart updates (quantity changes) and reload page to sync gift wrapping
     */
    listenForCartUpdates() {
      // Flag to prevent multiple reloads
      this.isReloading = false;
      
      // Listen for cart:updated event (fires after AJAX cart update completes)
      document.addEventListener('cart:updated', (evt) => {
        if (this.isReloading) return;
        this.checkIfKadobonItemsChanged(evt.detail?.cart);
      });
      
      // Also listen for ajaxCart:updated (some themes use this)
      document.addEventListener('ajaxCart:updated', (evt) => {
        if (this.isReloading) return;
        this.checkIfKadobonItemsChanged(evt.detail?.cart);
      });
    }
    
    /**
     * Check if kadobon items changed and reload if needed
     */
    async checkIfKadobonItemsChanged(cartData) {
      try {
        // Get fresh cart data if not provided
        const cart = cartData || await fetch(this.config.routes.cart + '.js').then(r => r.json());
        
        // Count kadobon items in current cart (excluding gift wrap products)
        let currentKadobonCount = 0;
        cart.items.forEach(item => {
          if (item.product_id !== this.config.giftWrapProductId) {
            // Check if this product is in our kadobon items list
            const isKadobon = this.config.kadobonItems.some(kadobonData => {
              const parts = kadobonData.split('::');
              return parts[2] && parseInt(parts[2]) === item.product_id;
            });
            if (isKadobon) {
              currentKadobonCount += item.quantity;
            }
          }
        });
        
        // Compare with initial count
        const initialKadobonCount = this.config.kadobonCount;
        
        if (currentKadobonCount !== initialKadobonCount) {
          console.log('Kadobon items changed, reloading page...');
          this.isReloading = true;
          window.location.reload();
        }
      } catch (error) {
        console.error('Error checking kadobon items:', error);
      }
    }
  }

  // Initialize when DOM is ready
  function initGiftWrapping() {
    const container = document.querySelector('[data-gift-wrapping]');
    if (container && !container.dataset.initialized) {
      container.dataset.initialized = 'true';
      new GiftWrapping(container);
    }
  }

  // Initialize on page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initGiftWrapping);
  } else {
    initGiftWrapping();
  }
})();
